import './_styles.scss';
import './_styles.dark-mode.scss';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import arrayMove from 'array-move';
import qs from 'qs';

import Loader from '../../../components/Loaders/Loader';
import FilterGroup from '../../../components/Filters/FilterGroup';
import Table from './components/Table';
import ModalsManager from './components/Modals/Manager';

import {
  getPaymentsList,
  getPaymentsMethodsList,
  getPayment,
  addPayment,
  sortPayments,
  toggleVisibilityPayment,
  togglePossibilityOfUsePayment,
  editPayment,
} from '../../../services/endpoints/settings';
import { settingsSelector } from '../../../redux/selectors/settings';
import { updateSelectedItem } from './helpers';

const Payments = () => {
  const location = useLocation();
  const settings = useSelector((state) => settingsSelector(state));
  const { mainConfig: config } = settings;
  const { access, languages, default_language } = config;

  const queryString = qs.parse(location.search.slice(1));

  const [data, setData] = useState([]);
  const [modalData, setModalData] = useState({});
  const [methods, setMethods] = useState();
  const [openModal, setOpenModal] = useState('');
  const [modalError, setModalError] = useState('');
  const [isLoading, setIsLoading] = useState({
    page: true,
    table: false,
    submit: false,
    modal: false,
    modalSubmit: false,
    showData: false,
  });

  const onHideModals = () => {
    setOpenModal('');
  };

  const fetchList = async (loader = true, loaderTable = false) => {
    loader && setIsLoading((prevState) => ({ ...prevState, page: true }));
    loaderTable && setIsLoading((prevState) => ({ ...prevState, table: true }));
    try {
      const response = await getPaymentsList(queryString);
      if (response.success) {
        setData(response.data);
        response.data?.payments?.forEach((method) => {
          if (modalData?.item?.id === method.id) {
            setModalData((prevState) => ({ ...prevState, item: method }));
          }
        });
      }
      loader && setIsLoading((prevState) => ({ ...prevState, page: false }));
      loaderTable && setIsLoading((prevState) => ({ ...prevState, table: false }));
    } catch (error) {
      loader && setIsLoading((prevState) => ({ ...prevState, page: false }));
      loaderTable && setIsLoading((prevState) => ({ ...prevState, table: false }));
    }
  };

  const fetchMethods = async () => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));
    try {
      const response = await getPaymentsMethodsList();
      setMethods(response.data);
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const fetchPaymentSU = async () => {
    setIsLoading((prevState) => ({ ...prevState, showData: true }));
    setModalData((prevState) => ({ ...prevState, showData: false }));
    try {
      const response = await getPayment(modalData?.item?.id);
      setModalData((prevState) => ({
        ...prevState,
        showData: true,
        fieldsToUpdate: response?.data,
        item: { ...prevState.item, details: updateSelectedItem(modalData?.item?.details, response?.data) },
      }));
      setIsLoading((prevState) => ({ ...prevState, showData: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, showData: false }));
    }
  };

  const handleSort = async ({ oldIndex, newIndex }) => {
    setIsLoading((prevState) => ({ ...prevState, table: true }));
    const temp = data.payments;
    const currentItem = data?.payments?.[oldIndex];
    const items = arrayMove(data.payments, oldIndex, newIndex);
    setData((prevState) => ({ ...prevState, payments: items }));

    try {
      await sortPayments(currentItem.id, newIndex + 1);
      await fetchList(false);
      setIsLoading((prevState) => ({ ...prevState, table: false }));
    } catch (error) {
      setData((prevState) => ({ ...prevState, payments: temp }));
      setIsLoading((prevState) => ({ ...prevState, table: false }));
    }
  };

  const onChangeVisibility = async (event, payment) => {
    event.persist();
    event.target.disabled = true;
    event?.target?.setAttribute('disabled', 'disabled');
    setData((prevState) => ({
      ...prevState,
      payments: prevState?.payments?.map((item) => {
        if (item.id === payment.id) {
          return {
            ...item,
            visibility: !item.visibility,
          };
        }
        return item;
      }),
    }));

    try {
      await toggleVisibilityPayment(payment.id, !payment.visibility);
    } catch (error) {
      await fetchList(false);
    } finally {
      event.target.disabled = false;
      event?.target?.removeAttribute('disabled');
    }
  };

  const handleChangeAllow = (key, item) => {
    setModalData({ actionKey: key, actionType: key, item });

    switch (key) {
    case 'allow':
    case 'disallow':
      setOpenModal('confirm');
      break;

    default:
      break;
    }
  };

  const togglePossibility = async () => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    const possibility = modalData.actionKey === 'allow';
    try {
      await togglePossibilityOfUsePayment(modalData.item.id, possibility);
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      onHideModals('');
      await fetchList(false, true);
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      onHideModals('');
    }
  };

  const onAddSubmit = async (method_id) => {
    setModalError('');
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await addPayment(method_id);
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      onHideModals('');
      await fetchList(false, true);
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  };

  const onEditSubmit = async (payload, hideModal) => {
    setModalError('');
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await editPayment(modalData?.item?.id, payload);
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      hideModal && onHideModals('');
      await fetchList(false, true);
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  };

  const onSubmitModal = (type, data, hideModal) => {
    switch (type) {
    case 'confirm':
      togglePossibility();
      break;

    case 'add':
      onAddSubmit(data);
      break;

    case 'edit':
      onEditSubmit(data, hideModal);
      break;

    default:
      break;
    }
  };

  const onShowModal = () => {
    switch (openModal) {
    case 'add':
      fetchMethods();
      break;

    default:
      break;
    }
  };

  const onEdit = (item) => {
    setModalData({ actionKey: 'edit', actionType: 'edit', item, languages, default_language });
    setOpenModal('edit');
  };

  useEffect(() => {
    fetchList();
    document.title = 'Payments';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="settings-payments">
      <ModalsManager
        access={access}
        open={openModal}
        isLoading={isLoading.modal}
        isLoadingSubmit={isLoading.modalSubmit}
        isLoadingData={isLoading.showData}
        modalData={modalData}
        methods={methods}
        error={modalError}
        fetchPaymentSU={fetchPaymentSU}
        onSubmit={onSubmitModal}
        onShow={onShowModal}
        onHide={onHideModals}
      />

      {isLoading.page ? (
        <div className="loader-container" style={{ minHeight: '300px' }}>
          <Loader />
        </div>
      ) : (
        <>
          <FilterGroup>
            {access?.payments_add ? (
              <Button onClick={() => setOpenModal('add')} className="pull-left add-btn">
                Add method
              </Button>
            ) : null}
          </FilterGroup>

          {access?.payments_list ? (
            <Table
              isLoading={isLoading.table}
              access={access}
              items={data?.payments}
              handleSort={handleSort}
              onChangeVisibility={onChangeVisibility}
              handleChangeAllow={handleChangeAllow}
              onEdit={onEdit}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default Payments;
